import type { QuestOutcome } from '../../questResults'
import type { TeamId } from '../../teams'
import type { Game } from '../../types'

// 'dimmed' is the pre-start locked state — the gameweek has
// been started but not every fixture predicted yet. Distinct from 'locked',
// which is the post-deadline freeze (steel flow border + a modest dim step,
//); the two share no border/wash/pump/particle treatment.
// 'result' is the post-reveal state — the sealed steel edge
// resolves into a solid teal/coral edge; see `QuestResultData` below. It
// also covers quests the user never unlocked (`outcome: 'unplayed'`).
export type QuestPhase = 'dimmed' | 'cta' | 'confirm' | 'locked' | 'result'

// Defined in src/questResults.ts because the server grades against the same union
// — re-exported here so the card's own type importers keep reading from one place. 'void' is the
// fourth state that ticket added: the round's data could not answer the quest, so it awards
// nothing to anyone rather than reading as a miss.
export type { QuestOutcome }

export interface QuestResultData {
  outcome: QuestOutcome
  /** Points earned — 0 for miss/unplayed. */
  points?: number
  /** Points that were on offer — shown for 'unplayed' so a user can see what
   * unlocking would have earned them. */
  pointsAvailable?: number
  /** team pick: the true winning figure, e.g. "9 saves" — same content
   * whether hit or miss (the crest carries the hit/miss distinction). A quest
   * with no winner puts its verdict here on a miss instead ("Not ahead at
 * HT"). */
  statLine?: string
  /** team pick: the caption under `statLine`. `undefined` leaves `TeamResult`
   * to derive it from the outcome; `null` suppresses it, for the miss that
   * states a verdict rather than a figure and so has nothing to label. */
  statCaption?: string | null
  /** match pick: truth text naming the result, e.g. "13 corners" (hit) or
   * "CHE v NEW · 14 corners" (miss/unplayed) — never a second pair of
   * crests, per the epic's shared footprint rule. */
  truthLine?: string
  /** match pick miss only: the user's own figure for comparison, e.g. "Your
   * pick had 9". */
  yourFigureLine?: string
  /** over/under: the side that actually hit. */
  trueSide?: 'over' | 'under'
  /** over/under: the actual number, e.g. "4 braces". */
  trueValueLine?: string
}

export interface TeamQuestPick {
  type: 'team'
  /** clubBanners.ts key — undefined while unpicked. */
  team?: TeamId
}

export interface MatchQuestPick {
  type: 'match'
  /** The one fixture chosen — the "two shields" are its two sides, always
   * paired to a single Game, never two independent team picks. */
  game?: Game
}

export interface OverUnderQuestPick {
  type: 'over_under'
  /** Fixed per the quest definition (e.g. 2.5) — not player-editable. */
  line: number
  /** e.g. "GW PENALTIES" */
  eyebrow: string
  side?: 'over' | 'under'
}

export type QuestPick = TeamQuestPick | MatchQuestPick | OverUnderQuestPick

// One size, fixed across all three pick types and every state (// replacing the "hero-sized" rule
// now that cards sit two to a row instead of full width). Set by the tightest case — match pick's
// two crests plus a V divider in half a column — so team pick doesn't run larger just because it
// has room to spare; a mismatched pair reads as messy. Still the largest single element on the
// card, between the Profile header shield (44x56, Profile.css:55-56) and the FixturesPage
// fixture-row shield (26x33).
export const SHIELD_SIZE = 40
